import React, { Fragment } from 'react';
import { reduxForm } from 'redux-form';

import validate from '../validate';

import FlatButton from 'material-ui/FlatButton';

import { toFriendlyDateString, formatMoney, titleize } from 'helpers/Formatter';
import { filterObject } from 'helpers/utilities';
import { AccentColor } from 'helpers/AccentColorHelper';
import { hostIsFourStar } from 'helpers/CurrentWindowLocation';
import { getTranslatedString } from 'helpers/i18n';

const style = {
  button: {
    color: 'white',
    float: 'right',
    height: '40px',
  },
  backButton: {
    float: 'left',
    color: AccentColor(),
    height: '40px',
  },
  leaseDetails: {
    fontFamily: 'Circular',
  },
  leaseEmphasis: {
    fontFamily: 'Circular-Black',
  },
};

const TENANT_SERVICES = {
  gas: `${getTranslatedString('tenantPortal.application.leaseDetails.gas')}`,
  guest_parking: `${getTranslatedString('tenantPortal.application.leaseDetails.guestParking')}`,
  storage_space: `${getTranslatedString('tenantPortal.application.leaseDetails.storageSpace')}`,
  on_site_laundry: `${getTranslatedString('tenantPortal.application.leaseDetails.onsiteLaundry')}`,
  air_conditioning: `${getTranslatedString(
    'tenantPortal.application.leaseDetails.airConditioning'
  )}`,
};

const renderTenantServices = (servicesAndUtilities) => {
  if (!servicesAndUtilities || !servicesAndUtilities['services']) return null;

  const includedServices = filterObject(
    servicesAndUtilities['services'],
    (service) => service.is_included
  );

  const includedOtherServices = (servicesAndUtilities['services']['other'] || [])
    .filter((service) => service.is_included)
    .map((otherService) => otherService['name']);

  const tenantServicesArray = Object.keys(includedServices).concat(includedOtherServices);

  return tenantServicesArray.map((service, index) => {
    const commaOrEnd = index === tenantServicesArray.length - 1 ? '' : ', ';
    return (
      <span>
        <b style={style.leaseEmphasis}>
          {titleize(TENANT_SERVICES[service] || service)}
          {commaOrEnd}
        </b>
      </span>
    );
  });
};

const renderTenantUtilities = (servicesAndUtilities) => {
  if (!servicesAndUtilities || !servicesAndUtilities['utilities']) return null;

  const utilityArrangement = servicesAndUtilities['utilities'];

  const TENANT_UTILITIES = {
    landlord: `${getTranslatedString('tenantPortal.application.leaseDetails.landlord')}`,
    tenant: `${getTranslatedString('tenantPortal.application.leaseDetails.tenant')}`,
  };

  return Object.keys(utilityArrangement)
    .filter((utility) => {
      return utilityArrangement[utility].paid_by;
    })
    .map((utility, _) => (
      <p style={{ margin: '10px 0' }}>
        {titleize(utility.split('_').join(' '))}{' '}
        {getTranslatedString('tenantPortal.application.leaseDetails.paidBy')}{' '}
        <b style={style.leaseEmphasis}>
          {TENANT_UTILITIES[utilityArrangement[utility].paid_by] ||
            titleize(utilityArrangement[utility].paid_by)}
        </b>
      </p>
    ));
};

const renderLeaseItems = (leaseItems) => {
  if (!leaseItems || !leaseItems.length) return null;

  return leaseItems.map((leaseItem) => (
    <p style={{ margin: '10px 0' }}>
      <b style={style.leaseEmphasis}>{titleize(leaseItem.name)}</b>
    </p>
  ));
};

const renderRentalIncentive = (rentalIncentives) => {
  if (!rentalIncentives) return null;
  return rentalIncentives.map((rentalIncentive) => {
    return (
      <div className="row">
        <h4 style={style.leaseDetails}>
          <p style={{ margin: '10px 0' }}>
            {getTranslatedString('tenantPortal.application.leaseDetails.incentive')}
            <b style={style.leaseEmphasis}> {rentalIncentive.amount_formatted} </b>
            {getTranslatedString('tenantPortal.application.leaseDetails.incentiveFor')}
            <b style={style.leaseEmphasis}> {rentalIncentive.frequency} </b>
            {getTranslatedString('tenantPortal.application.leaseDetails.incentiveStart')}
            <b style={style.leaseEmphasis}>
              {' '}
              {toFriendlyDateString(rentalIncentive.effective_date)}{' '}
            </b>
          </p>
        </h4>
      </div>
    );
  });
};

const renderProratedRent = (proratedRent) => {
  if (!proratedRent) return null;
  return (
    <div className="row">
      <h4 style={style.leaseDetails}>
        {getTranslatedString('tenantPortal.application.leaseDetails.proratedRent')}{' '}
        <b style={style.leaseEmphasis}>{proratedRent}</b>
      </h4>
    </div>
  );
};

const WizardFormLeaseDetailsPage = (props) => {
  const { handleSubmit, previousPage, leaseDetails, applicationDetails, propertyName } = props;
  const isGenericApplicationLink =
    applicationDetails.is_generic ?? !!leaseDetails?.lease_start_date === false;

  const smokingPolicy = leaseDetails.smoking_policy
    ? `${getTranslatedString('tenantPortal.application.leaseDetails.smoking')}`
    : `${getTranslatedString('tenantPortal.application.leaseDetails.noSmoking')}`;
  const isLastMonthsRentVerbiage = leaseDetails.last_months_rent
    ? `${getTranslatedString('tenantPortal.application.leaseDetails.lastMonthRent')}`
    : `${getTranslatedString('tenantPortal.application.leaseDetails.securityDeposit')}`;
  const securityOrLastMonthsRentVerbiage = leaseDetails.last_months_rent
    ? `${getTranslatedString('tenantPortal.application.leaseDetails.lastMonthRent')}`
    : `${getTranslatedString('tenantPortal.application.leaseDetails.securityDeposit')}`;
  const includedServices = renderTenantServices(leaseDetails.services_and_utilities);
  const includedUtilities = renderTenantUtilities(leaseDetails.services_and_utilities);
  const leaseItems = renderLeaseItems(leaseDetails.lease_items);
  const includesFirstMonthsRent = leaseDetails.include_first_months ? 'first months rent and ' : '';
  const rentalIncentive = renderRentalIncentive(leaseDetails.rental_incentives);
  const proratedRent = leaseDetails.create_prorated_charge
    ? renderProratedRent(leaseDetails.prorated_rent)
    : null;

  // we are doing this for fourstar specfic asks around verbiage
  // this should be replace and come up from the backend in the App and Leasing update
  const fourStarInsuranceVerbiage = (
    <h4 style={style.leaseDetails}>
      <b style={style.leaseEmphasis}>
        {getTranslatedString('tenantPortal.application.leaseDetails.insuranceNote')}
      </b>
      {getTranslatedString('tenantPortal.application.leaseDetails.insuranceFourstar')}
    </h4>
  );
  const yuhuInsuranceVerbiage = (
    <h4 style={style.leaseDetails}>
      <b style={style.leaseEmphasis}>
        {getTranslatedString('tenantPortal.application.leaseDetails.insuranceNote')}{' '}
      </b>
      {getTranslatedString('tenantPortal.application.leaseDetails.insuranceYuhu')}{' '}
      <b style={style.leaseEmphasis}>
        {getTranslatedString('tenantPortal.application.leaseDetails.insuranceYuhuKeys')}
      </b>
    </h4>
  );
  const insuranceVerbiage = hostIsFourStar() ? fourStarInsuranceVerbiage : yuhuInsuranceVerbiage;

  return (
    <div style={{ marginTop: '50px' }}>
      <div>
        <h3
          style={{
            fontSize: '30px',
            marginBottom: '20px',
            fontFamily: 'Circular-Bold',
          }}
        >
          {isGenericApplicationLink
            ? `${getTranslatedString('tenantPortal.application.leaseDetails.application', {
                property: propertyName,
              })}`
            : `${getTranslatedString('tenantPortal.application.leaseDetails.confirm')}`}
        </h3>
      </div>
      <form onSubmit={handleSubmit}>
        {!isGenericApplicationLink && (
          <Fragment>
            <div className="row">
              <h4 style={style.leaseDetails}>
                {getTranslatedString('tenantPortal.application.leaseDetails.unitDetails')}
                {leaseDetails.delay_unit_assignment && (
                  <span>
                    {getTranslatedString('tenantPortal.application.leaseDetails.unitDetails.a')}{' '}
                    <b style={style.leaseEmphasis}>
                      {leaseDetails.unit_type_name}{' '}
                      {getTranslatedString(
                        'tenantPortal.application.leaseDetails.unitDetails.unit'
                      )}{' '}
                    </b>
                  </span>
                )}
                {!leaseDetails.delay_unit_assignment && (
                  <b style={style.leaseEmphasis}>
                    {' '}
                    {getTranslatedString(
                      'tenantPortal.application.leaseDetails.unitDetails.unit'
                    )}{' '}
                    {leaseDetails.unit_number}
                  </b>
                )}{' '}
                {getTranslatedString('tenantPortal.application.leaseDetails.unitDetails.location')}{' '}
                <b style={style.leaseEmphasis}>{leaseDetails.property_name}.</b>
              </h4>
            </div>
            <div className="row">
              <h4 style={style.leaseDetails}>
                {getTranslatedString('tenantPortal.application.leaseDetails.start')}
                <b style={style.leaseEmphasis}>
                  {' '}
                  {toFriendlyDateString(leaseDetails.lease_start_date)}
                </b>{' '}
                {getTranslatedString('tenantPortal.application.leaseDetails.end')}
                <b style={style.leaseEmphasis}>
                  {' '}
                  {toFriendlyDateString(leaseDetails.lease_end_date)}.
                </b>
              </h4>
            </div>
            {leaseDetails.early_move_in_date && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.moveDate')}{' '}
                  <b style={style.leaseEmphasis}>
                    {toFriendlyDateString(leaseDetails.early_move_in_date)}
                  </b>
                </h4>
              </div>
            )}
            <div className="row">
              <h4 style={style.leaseDetails}>
                {getTranslatedString('tenantPortal.application.leaseDetails.rentAmount')}{' '}
                <b style={style.leaseEmphasis}>{leaseDetails.monthly_rent}</b>{' '}
                {getTranslatedString('tenantPortal.application.leaseDetails.rentFrequency')}
              </h4>
            </div>
            <div className="row">
              <h4 style={style.leaseDetails}>
                {getTranslatedString('tenantPortal.application.leaseDetails.maxRent')}{' '}
                <b style={style.leaseEmphasis}>{leaseDetails.deposit}</b>{' '}
                {getTranslatedString('tenantPortal.application.rentDetails')}{' '}
                {isLastMonthsRentVerbiage}
              </h4>
            </div>
            {leaseDetails.key_deposit && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.key')}{' '}
                  <b style={style.leaseEmphasis}>{leaseDetails.key_deposit}</b>{' '}
                  {getTranslatedString('tenantPortal.application.leaseDetails.keyDeposit')}
                </h4>
              </div>
            )}
            {rentalIncentive}
            {proratedRent}
            {!hostIsFourStar() && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.propertyDetails')}{' '}
                  <b style={style.leaseEmphasis}>{smokingPolicy}</b>{' '}
                  {getTranslatedString('tenantPortal.application.leaseDetails.property')}
                </h4>
              </div>
            )}
            {includedServices && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.services')}{' '}
                  {includedServices}
                </h4>
              </div>
            )}
            {includedUtilities && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.utilities')}{' '}
                  {includedUtilities}
                </h4>
              </div>
            )}
            {leaseItems && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.items')} {leaseItems}
                </h4>
              </div>
            )}
            {applicationDetails.use_payment_platform && (
              <div className="row">
                <h4 style={style.leaseDetails}>
                  {getTranslatedString('tenantPortal.application.leaseDetails.payment')}{' '}
                  <b style={style.leaseEmphasis}>
                    {formatMoney(applicationDetails.total_application_payable)}
                  </b>{' '}
                  {getTranslatedString('tenantPortal.application.leaseDetails.paymentInfo', {
                    firstMonth: includesFirstMonthsRent,
                    security: securityOrLastMonthsRentVerbiage,
                  })}
                  <b style={style.leaseEmphasis}>
                    {getTranslatedString('tenantPortal.application.leaseDetails.declinedPayment')}
                  </b>
                </h4>
              </div>
            )}
            <div className="row">{insuranceVerbiage}</div>
          </Fragment>
        )}
        {isGenericApplicationLink && (
          <div className="row">
            <h4 style={style.leaseDetails}>
              {getTranslatedString('tenantPortal.application.leaseDetails.headsUp')}
            </h4>
          </div>
        )}
        <div className="row" style={{ marginTop: '70px' }} />
        <div id="application_footerbar">
          <div style={{ textAlign: 'center', maxWidth: '600px', margin: 'auto' }}>
            <FlatButton
              label={getTranslatedString('tenantPortal.application.leaseDetails.button.back')}
              type="button"
              className="previous"
              onClick={previousPage}
              backgroundColor="#fff"
              hoverColor="grey"
              style={style.backButton}
            />
            <FlatButton
              label={
                isGenericApplicationLink
                  ? `${getTranslatedString(
                      'tenantPortal.application.leaseDetails.button.continue'
                    )}`
                  : `${getTranslatedString(
                      'tenantPortal.application.leaseDetails.button.looksGood'
                    )}`
              }
              type="submit"
              className="next"
              primary={true}
              backgroundColor={AccentColor()}
              hoverColor="grey"
              style={style.button}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default reduxForm({
  form: 'wizard',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
})(WizardFormLeaseDetailsPage);
